// Type definitions for tenant API
// api/tenantApi.ts
// Function signatures are typically defined in the actual API file
import { AxiosResponse } from 'axios';
import { config } from 'process';
import { get, post, put, patch, del, getRaw } from './apiClient';

/**
 * Authentication provider configuration
 */
// Real auth provider types stored in the database.
// OTP and magic link are NOT stored here — they are controlled by keycloak.clients.externalOtp/externalMagicLink.
export interface AuthProvider {
    type: 'credential' | 'oidc';
    providerId: string;
    label: string;
    fields?: { [key: string]: { label: string; type: string } };
    // OIDC fields
    clientId?: string;
    clientSecret?: string;
    tenantId?: string;       // Azure AD tenant / directory ID
    providerName?: string;   // e.g. "AzureAD"
    alias?: string;          // Keycloak IDP alias
    // Full OIDC config (optional — for custom providers)
    authorizationUrl?: string;
    tokenUrl?: string;
    userInfoUrl?: string;
    issuer?: string;
    defaultScope?: string;
    enabled?: boolean;
}

/**
 * Federated AD configuration for Microsoft OIDC identity provider
 */
export interface FederatedAdConfig {
    alias: string;
    displayName: string;
    clientId: string;
    clientSecret: string;
    // Azure AD: provide azureTenantId and endpoints are computed automatically.
    // Generic OIDC: leave azureTenantId empty and provide explicit endpoint URLs.
    azureTenantId?: string;
    defaultScopes?: string;
    syncMode?: string;
    // Generic OIDC explicit endpoints (override computed Azure AD values):
    authorizationUrl?: string;
    tokenUrl?: string;
    jwksUrl?: string;
    issuer?: string;
}

export type IdpType = 'azure-ad' | 'oidc';

/**
 * Authentication methods selection for provisioning and Keycloak sync
 */
export interface AuthMethodsConfig {
    credential?: boolean;
    otp?: boolean;
    magicLink?: boolean;
    federatedAd?: FederatedAdConfig | null;
}

/**
 * Request body for syncing auth config with Keycloak on an existing tenant.
 * keycloakAdminUrl / keycloakAdminPassword are optional — the server uses its own
 * configured KC admin credentials when they are omitted (KEYCLOAK_ADMIN_URL / KEYCLOAK_ADMIN_PASSWORD).
 * Pass them explicitly only for a one-off manual override.
 */
export interface SyncAuthRequest {
    keycloakAdminUrl?: string;
    keycloakPublicUrl?: string;
    keycloakAdminUser?: string;
    keycloakAdminPassword?: string;
    protocol?: string;
    authMethods: AuthMethodsConfig;
}

/**
 * Response from Keycloak auth sync
 */
export interface SyncAuthResponse {
    data: Tenant;
    keycloakSync: {
        extMagicSecret: string;
        extOtpSecret: string;
        federatedAdRedirectUri: string | null;
    };
    message: string;
}

/**
 * Tenant key configuration
 */
export interface TenantKeys {
    SignatureKeyManager: string;
    EncryptionKeyManager: string;
}

/**
 * Tenant email configuration
 */
export interface TenantEmail {
    sendEmailNotificationAddress?: string;
    sendEmailNotifcationDisplayName?: string;
    acsEndpoint?: string | null;
}

/**
 * Custom domain configuration
 */
export interface CustomDomain {
    domain: string;
    verified: boolean;
    createdAt: string;
}

/**
 * Per-tenant session security policy.
 * Stored under the 'sessionPolicy' field in the Tenant MongoDB document.
 */
export interface SessionPolicy {
    /** Allow users to have more than one active session simultaneously. */
    allowConcurrentSessions: boolean;
    /** Maximum concurrent sessions. null = unlimited. */
    maxConcurrentSessions: number | null;
    /** Hard cap on session extensions (sliding-window renewals). null = unlimited. */
    maxExtensions: number | null;
    /** Allow users to check "remember me" at login. */
    rememberMeEnabled: boolean;
    /** Duration of remember-me sessions in days. */
    rememberMeDurationDays: number;
    /** Default inactivity window for normal sessions (minutes). */
    defaultSessionDurationMinutes: number;
    /**
     * GDPR opt-in: store the client IP address with each session.
     * Justified by legitimate interest (security/audit) under Art. 6(1)(f).
     * Default: false — tenant admin must explicitly enable.
     */
    trackIpAddress: boolean;
    /** Retention period in days for stored IP addresses. */
    ipRetentionDays: number;
    /**
     * When true, BFF session expiry (timeout, idle) also kills the Keycloak SSO session.
     * Forces full credential re-entry on next login. Recommended for GRC platforms.
     */
    requireReauthOnSessionExpiry: boolean;
    // Keycloak realm timeout mirrors — null = use Keycloak realm default
    ssoSessionIdleTimeoutSeconds: number | null;
    ssoSessionMaxLifespanSeconds: number | null;
    accessTokenLifespanSeconds: number | null;
    clientSessionIdleTimeoutSeconds: number | null;
    clientSessionMaxLifespanSeconds: number | null;
    // Offline session timeouts — must equal rememberMeDurationDays (in seconds).
    // Controls how long offline tokens (remember-me) remain valid in Keycloak.
    offlineSessionIdleTimeoutSeconds: number | null;
    offlineSessionMaxLifespanSeconds: number | null;
}

export const defaultSessionPolicy: SessionPolicy = {
    allowConcurrentSessions: true,
    maxConcurrentSessions: null,
    maxExtensions: null,
    rememberMeEnabled: false,
    rememberMeDurationDays: 30,
    defaultSessionDurationMinutes: 15,
    trackIpAddress: false,
    ipRetentionDays: 90,
    requireReauthOnSessionExpiry: false,
    ssoSessionIdleTimeoutSeconds: null,
    ssoSessionMaxLifespanSeconds: null,
    accessTokenLifespanSeconds: null,
    clientSessionIdleTimeoutSeconds: null,
    clientSessionMaxLifespanSeconds: null,
    offlineSessionIdleTimeoutSeconds: null,
    offlineSessionMaxLifespanSeconds: null,
};

/**
 * Tenant data model
 */
export interface Tenant {
    _id: string;
    tenantId: string;
    name?: string;
    baseUrl?: string;
    subdomain: string;
    // environmentTag / platformAdmin exist in the Mongoose schema (server/models/Tenant.ts)
    // and in updateTenant's `allowed` array, but were never typed here — the same
    // allow-list/interface omission bug class that hides fields from the client.
    // environmentTag is the namespace allow-list token the Platform reads as
    // TenantsSettings.EnvironmentTag; absent/unmatched means no tagged runtime loads
    // this tenant. It is DERIVED (environment -> namespace -> tag), never free text.
    environmentTag?: string;
    // Inputs the SysAdmin role seed was built from (Keycloak group + IdP alias).
    // No default anywhere, deliberately: a literal default would make dot-notation
    // $set updates fail with PathNotViable.
    platformAdmin?: {
        groupName?: string;
        idpAlias?: string;
        /** Always the literal 'SysAdmin' — three server-side short-circuits string-match it. */
        roleId?: string;
        seededAt?: string;
    };
    serviceName: string;
    servicePort: number;
    nodeSelector: string;
    databaseName: string;
    authenticationProviders: AuthProvider[];
    keys: TenantKeys;
    cluster: string;
    email: TenantEmail;
    status: 'active' | 'disabled' | 'suspended' | 'maintenance' | 'provisioning' | 'decommissioning' | 'deleted';
    statusReason?: string;
    statusUpdatedAt?: string;
    maintenanceToken?: string;
    customDomains?: CustomDomain[];
    dnsCreated: boolean;
    createK8sIngress: boolean;
    useSsl?: boolean;
    port?: number | null;
    secretStoreName?: string;
    provisioning?: {
        internal?: { mode: 'AutoProvision' | 'InviteOnly' | 'Disabled' };
        external?: { mode: 'AutoProvision' | 'InviteOnly' | 'Disabled' };
        // Phase 1 provisioning step ledger (server/controllers/tenantProvisionController.ts).
        // Carries the terminal `namespace-restart` step the readiness panel surfaces as a banner.
        steps?: ProvisioningStep[];
    };
    redis?: { username: string; passwordSecretKey: string };
    security?: { algorithm: string; ecCurve: string; keyRotationDays: number; keyGracePeriodDays: number };
    signatureProvider?: SignatureProviderSettings | null;
    sessionPolicy?: SessionPolicy;
    dedicatedDeploymentObjectTypes?: string[] | null;
    /** Tenant-level sub-tenant scoping switch; absent means off. */
    subtenantsEnabled?: boolean;
    createdAt: string;
    updatedAt: string;
    deletedAt?: string;
}

/**
 * Test tenant provider endpoint - Get tenant ID by domain
 * @param tenantDomain The domain to look up (e.g., "dev.ontoris.com")
 * @param apiKey Optional API key for the public endpoint
 * @returns Promise with tenant lookup result
 */
export const testTenantProvider = (
    tenantDomain: string
): Promise<{ tenantId: string }> => {

     
    return getRaw<{ tenantId: string }>(
        `/tenantsProvider/${tenantDomain}`,
       
    );
};

/**
 * Data for creating a new tenant
 */
export interface TenantCreate {
    tenantId?: string; // Optional - will be auto-generated if not provided
    name?: string;
    subdomain: string;
    databaseName: string;
    authenticationProviders: AuthProvider[];
    authMethods?: AuthMethodsConfig; // passed to /provision endpoint
    keys?: TenantKeys;
    cluster: string;
    email?: TenantEmail;
    status?: 'active' | 'disabled' | 'maintenance';
    createDnsEntry?: boolean; // Whether to create a DNS entry for the subdomain
}

/**
 * Data for updating an existing tenant
 */
export interface TenantUpdate {
    tenantId?: string;
    name?: string;
    subdomain?: string;
    databaseName?: string;
    authenticationProviders?: AuthProvider[];
    keys?: TenantKeys;
    cluster?: string;
    email?: TenantEmail;
    status?: 'active' | 'disabled' | 'maintenance';
}

/**
 * Data for changing tenant status
 */
export interface StatusChangeRequest {
    status: 'active' | 'disabled' | 'maintenance';
    reason?: string;
}

/**
 * Response for maintenance token
 */
export interface MaintenanceTokenResponse {
    token: string;
    expiresAt: string | null;
}

/**
 * Standard API response format
 */
export interface ApiResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}



/**
 * Get all tenants
 * @returns Promise with tenants array
 */
export const getAllTenants = (): Promise<ApiResponse<Tenant[]>> => {
    return get<ApiResponse<Tenant[]>>('/tenants');
};

/**
 * Get tenant by ID
 * @param id Tenant ID
 * @returns Promise with tenant data
 */
export const getTenantById = (id: string): Promise<ApiResponse<Tenant>> => {
    return get<ApiResponse<Tenant>>(`/tenants/${id}`);
};

/**
 * Create a new tenant
 * @param tenantData Tenant data
 * @returns Promise with created tenant
 */
export const createTenant = (tenantData: TenantCreate): Promise<ApiResponse<Tenant>> => {
    return post<ApiResponse<Tenant>>('/tenants', tenantData);
};

/**
 * Update a tenant
 * @param id Tenant ID
 * @param updateData Data to update
 * @returns Promise with updated tenant
 */
export const updateTenant = (id: string, updateData: TenantUpdate): Promise<ApiResponse<Tenant>> => {
    return put<ApiResponse<Tenant>>(`/tenants/${id}`, updateData);
};

/**
 * Change tenant status
 * @param id Tenant ID
 * @param statusData Status change data
 * @param includeToken Whether to include maintenance token in response
 * @returns Promise with updated tenant
 */
export const changeTenantStatus = (
    id: string,
    statusData: StatusChangeRequest,
    includeToken: boolean = false
): Promise<ApiResponse<Tenant>> => {
    const url = includeToken
        ? `/tenants/${id}/status?includeToken=true`
        : `/tenants/${id}/status`;

    return patch<ApiResponse<Tenant>>(url, statusData);
};

/**
 * Get maintenance token for a tenant
 * @param id Tenant ID
 * @returns Promise with maintenance token
 */
export const getMaintenanceToken = (id: string): Promise<ApiResponse<MaintenanceTokenResponse>> => {
    return get<ApiResponse<MaintenanceTokenResponse>>(`/tenants/${id}/maintenance-token`);
};

/**
 * Delete a tenant (soft delete)
 * @param id Tenant ID
 * @returns Promise with deletion result
 */
export const deleteTenant = (id: string): Promise<ApiResponse<{ message: string }>> => {
    return del<ApiResponse<{ message: string }>>(`/tenants/${id}`);
};

/**
 * Get all deleted tenants
 * @returns Promise with deleted tenants array
 */
export const getDeletedTenants = (): Promise<ApiResponse<Tenant[]>> => {
    return get<ApiResponse<Tenant[]>>('/tenants/deleted/all');
};

/**
 * Restore a deleted tenant
 * @param id Tenant ID
 * @returns Promise with restored tenant
 */
export const restoreTenant = (id: string): Promise<ApiResponse<Tenant>> => {
    return post<ApiResponse<Tenant>>(`/tenants/deleted/${id}/restore`, {});
};

/**
 * Permanently delete a tenant
 * @param id Tenant ID
 * @returns Promise with deletion result
 */
export const permanentlyDeleteTenant = (id: string): Promise<ApiResponse<{ message: string }>> => {
    return del<ApiResponse<{ message: string }>>(`/tenants/deleted/${id}`);
};

/**
 * Duplicate a tenant
 * @param id Tenant ID to duplicate
 * @param newData Override data for the new tenant
 * @returns Promise with new tenant
 */
export const duplicateTenant = (
    id: string,
    newData: Partial<TenantCreate>
): Promise<ApiResponse<Tenant>> => {
    return post<ApiResponse<Tenant>>(`/tenants/${id}/duplicate`, newData);
};

// ── Tenant Provisioning ─────────────────────────────────────────────────────

export interface ProvisionRequest {
    // Identity
    tenantId: string;
    displayName: string;
    // URLs
    baseUrl: string;              // e.g. acme.ontoris.local  (no protocol)
    protocol?: string;            // default: https
    keycloakAdminUrl: string;     // e.g. https://auth.acme.ontoris.local
    keycloakPublicUrl?: string;   // if different from adminUrl
    // Required only when local === true (admin-cli password grant against a fresh on-prem
    // realm). Ignored for cloud deployments, which use the platform's own service identity.
    keycloakAdminUser?: string;   // default: admin
    keycloakAdminPassword?: string;
    // Platform
    jwksHost?: string;            // default: identity-provider-proxy
    jwksPort?: number;            // default: 8080
    jwksBaseUrl?: string;         // full JWKS base URL — preferred over jwksHost/jwksPort
    // Deployment target
    environment?: 'prod' | 'dev' | 'demo';
    local?: boolean;              // true = on-prem/local deployment
    // MongoDB / infra
    databaseName: string;
    cluster: string;
    // OPTIONAL operator override. Omit it: the server derives the Azure Key Vault from the
    // deployment target (environment / local), which is what keeps a cloud tenant off the
    // local vault. Three live tenants were created with a hand-typed wrong value.
    secretStoreName?: string;
    // Email (optional)
    emailNotificationAddress?: string;
    emailNotificationDisplayName?: string;
    acsEndpoint?: string;
    // Auth methods selection
    authMethods?: AuthMethodsConfig;
    // BFF root-level fields
    useSsl?: boolean;
    port?: number | null;
    // Provisioning strategy
    provisioningInternalMode?: 'AutoProvision' | 'InviteOnly' | 'Disabled';
    provisioningExternalMode?: 'AutoProvision' | 'InviteOnly' | 'Disabled';
    // Redis (auto-generated if omitted)
    redisUsername?: string;
    redisPasswordKey?: string;
    // Security / JWT signing
    securityAlgorithm?: string;
    securityEcCurve?: string;
    securityKeyRotationDays?: number;
    securityKeyGracePeriodDays?: number;
}

export interface ProvisionResponse {
    tenantId: string;
    name: string;
    keycloak: {
        realmName: string;
        appClientId: string;
        appClientSecret: string;
        externalClientSecret: string;
        externalOtpClientSecret: string;
        emailSpiClientId: string;
        emailSpiSecret: string;
    };
    redis: { username: string; passwordSecretKey: string };
}

export const checkTenantIdAvailable = (tenantId: string): Promise<{ available: boolean }> =>
    get<{ available: boolean }>(`/tenants/check-id?tenantId=${encodeURIComponent(tenantId)}`);

export const checkDatabaseNameAvailable = (databaseName: string): Promise<{ available: boolean }> =>
    get<{ available: boolean }>(`/tenants/check-database?databaseName=${encodeURIComponent(databaseName)}`);

/**
 * Provision a new tenant: creates Keycloak realm + clients + flows and MongoDB document.
 */
export const provisionTenant = (
    data: ProvisionRequest
): Promise<ApiResponse<ProvisionResponse>> => {
    return post<ApiResponse<ProvisionResponse>>('/tenants/provision', data);
};

// ── Keycloak status check ────────────────────────────────────────────────────

export type KcComponentStatus = 'ok' | 'missing' | 'disabled' | 'not_configured' | 'error';
export type KcOverallStatus   = 'synced' | 'partial' | 'missing' | 'unreachable' | 'unconfigured';

export interface KcSyncCheckResult {
    checkedAt: string;
    overall: KcOverallStatus;
    realm: 'ok' | 'missing' | 'error';
    appClient: KcComponentStatus;
    otpClient: KcComponentStatus;
    magicLinkClient: KcComponentStatus;
    otpFlow: KcComponentStatus;
    magicLinkFlow: KcComponentStatus;
    wordAddinClient: KcComponentStatus;
    error?: string;
}

/**
 * Trigger a live Keycloak health-check for a tenant.
 * Stores the result in keycloak.sync.kcCheck and returns it.
 */
export const getKeycloakStatus = (id: string): Promise<ApiResponse<KcSyncCheckResult>> =>
    get<ApiResponse<KcSyncCheckResult>>(`/tenants/${id}/keycloak-status`);

/**
 * Reconcile Keycloak with the tenant's MongoDB document (MongoDB is master).
 * Creates/re-enables missing clients and flows.
 */
export const reconcileTenant = (id: string): Promise<ApiResponse<{ kcCheck: KcSyncCheckResult; message: string }>> =>
    post<ApiResponse<{ kcCheck: KcSyncCheckResult; message: string }>>(`/tenants/${id}/keycloak-reconcile`, {});

/**
 * Sync an existing tenant's authentication config with Keycloak.
 * Creates/enables Keycloak clients and flows based on authMethods selection.
 */
export const syncTenantAuth = (
    id: string,
    data: SyncAuthRequest
): Promise<ApiResponse<SyncAuthResponse>> => {
    return post<ApiResponse<SyncAuthResponse>>(`/tenants/${id}/sync-auth`, data);
};

export interface KcConnectionTestResult {
    ok: boolean;
    tokenOk: boolean;
    listRealmsOk: boolean;
    realmCount?: number;
    error?: string;
    hint?: string;
}

/**
 * Test Keycloak admin credentials for a tenant (validates token + list-realms).
 * Does NOT touch any tenant realm — safe to call anytime.
 */
export const testKeycloakConnection = (
    id: string,
    adminUrl?: string
): Promise<ApiResponse<KcConnectionTestResult>> =>
    post<ApiResponse<KcConnectionTestResult>>(`/tenants/${id}/keycloak-test-connection`, adminUrl ? { adminUrl } : {});

export interface JwksSyncResult {
    jwksBaseUrl: string;
    pattern: string;
    updated: string[];
    skipped: string[];
    failed: string[];
}

export const syncJwksUrls = (id: string): Promise<{ success: boolean; data?: JwksSyncResult; message?: string }> =>
    post(`/tenants/${id}/sync-jwks`, {});

// ── Diagnostics ──────────────────────────────────────────────────────────────

export interface DiagnosticCheckResult {
    status: 'ok' | 'error' | 'warning';
    message: string;
    latencyMs?: number;
    detail?: string;
}

export interface TenantDiagnosticsResult {
    status: 'ok' | 'degraded' | 'warning' | 'error';
    timestamp: string;
    tenantId: string;
    keycloakUrl: string;
    realmName: string;
    checks: Record<string, DiagnosticCheckResult>;
    message?: string;
    missingKeys?: string[];
    keyDetail?: Record<string, { status: string; ageInDays?: number; error?: string }>;
}

export const runTenantDiagnostics = (tenantId: string): Promise<ApiResponse<TenantDiagnosticsResult>> =>
    get(`/diagnostics/tenant/${tenantId}`);

export const initializeAllKeys = (tenantId: string): Promise<ApiResponse<{ initialized: number; total: number; results: Record<string, any> }>> =>
    post(`/key-management/${tenantId}/initialize-all`, {});

// ── Session Policy ───────────────────────────────────────────────────────────

/**
 * Update the BFF session policy for a tenant.
 */
export const updateSessionPolicy = (
    id: string,
    policy: Partial<SessionPolicy>
): Promise<ApiResponse<Tenant>> =>
    patch<ApiResponse<Tenant>>(`/tenants/${id}/session-policy`, policy);

// ── Security / Key Management Settings ──────────────────────────────────────

export const VALID_ALGORITHMS = ['ES256', 'ES384', 'ES512', 'RS256', 'RS384', 'RS512'] as const;
export type KeyAlgorithm = typeof VALID_ALGORITHMS[number];

export interface SecuritySettings {
    algorithm: KeyAlgorithm;
    ecCurve?: string;
    keyRotationDays: number;
    keyGracePeriodDays: number;
}

export const defaultSecuritySettings: SecuritySettings = {
    algorithm: 'ES256',
    ecCurve: 'P-256',
    keyRotationDays: 90,
    keyGracePeriodDays: 14,
};

export const updateLoginMethods = (
    id: string,
    credentialEnabled: boolean
): Promise<ApiResponse<Tenant>> =>
    patch<ApiResponse<Tenant>>(`/tenants/${id}/login-methods`, { credentialEnabled });

export const updateSecuritySettings = (
    id: string,
    settings: Partial<SecuritySettings>
): Promise<ApiResponse<Tenant>> =>
    patch<ApiResponse<Tenant>>(`/tenants/${id}/security-settings`, settings);

// ── Signature Provider Settings ─────────────────────────────────────────────

/** E-signature providers offered by the platform. Both have a live Workflow provider. */
export const SIGNATURE_PROVIDERS = ['None', 'Skribble', 'DocuSign'] as const;
export type SignatureProviderType = typeof SIGNATURE_PROVIDERS[number];

/**
 * Non-secret Skribble configuration.
 * apiKeySecretName / webhookSecretName are Vault key NAMES (pointers) — NOT the
 * secret values. The actual API key and webhook secret live in the tenant's Vault
 * under those names, mirroring how `redis.passwordSecretKey` works.
 */
export interface SkribbleProviderSettings {
    apiUsername: string;        // non-secret login identifier
    isDemoMode: boolean;
    apiKeySecretName: string;   // Vault key NAME
    webhookSecretName: string;  // Vault key NAME
    defaultMessage?: string;    // optional default signing message (non-secret)
}

/**
 * Non-secret DocuSign configuration. Mirrors Infrastructure `DocuSignProviderSettings`
 * (Ontoris.Common.Configuration.Settings/SignatureProviderSettings.cs) 1:1, camelCased.
 *
 * privateKeySecretName / webhookHmacKeySecretName are Vault key NAMES (pointers) — NOT
 * the values. The RSA private key PEM (1,700-3,250 chars, multi-line) and the Connect
 * HMAC key are seeded into the tenant Vault out-of-band; AppManagement never accepts,
 * transports or stores either one.
 */
export interface DocuSignProviderSettings {
    integratorKey: string;              // non-secret OAuth client / integration key GUID
    userId: string;                     // impersonated service-account user GUID (NOT impersonatedUserId)
    accountId: string;                  // DocuSign API account ID
    authServer: string;                 // account-d.docusign.com (demo) | account.docusign.com (prod)
    isDemoMode: boolean;
    privateKeySecretName: string;       // Vault key NAME
    webhookHmacKeySecretName: string;   // Vault key NAME (NOT webhookSecretName)
    defaultMessage?: string;            // optional default signing message (non-secret)
}

export interface SignatureProviderSettings {
    provider: SignatureProviderType;
    skribble?: SkribbleProviderSettings;
    docusign?: DocuSignProviderSettings;
}

export const defaultSkribbleSettings: SkribbleProviderSettings = {
    apiUsername: '',
    isDemoMode: false,
    apiKeySecretName: 'signature-skribble-apiKey',
    webhookSecretName: 'signature-skribble-webhookSecret',
};

export const defaultDocuSignSettings: DocuSignProviderSettings = {
    integratorKey: '',
    userId: '',
    accountId: '',
    authServer: 'account-d.docusign.com',
    isDemoMode: false,
    privateKeySecretName: 'signature-docusign-privateKey',
    webhookHmacKeySecretName: 'signature-docusign-webhookHmacKey',
};

export const defaultSignatureProviderSettings: SignatureProviderSettings = {
    provider: 'None',
    skribble: { ...defaultSkribbleSettings },
    docusign: { ...defaultDocuSignSettings },
};

/**
 * Update the tenant's e-signature provider + non-secret config.
 * Only Vault key NAMES are ever sent — never raw secret values.
 */
export const updateSignatureProviderSettings = (
    id: string,
    settings: SignatureProviderSettings
): Promise<ApiResponse<Tenant>> =>
    patch<ApiResponse<Tenant>>(`/tenants/${id}/signature-provider`, settings);

export interface KcSessionTimeouts {
    ssoSessionIdleTimeoutSeconds: number | null;
    ssoSessionMaxLifespanSeconds: number | null;
    accessTokenLifespanSeconds: number | null;
    clientSessionIdleTimeoutSeconds: number | null;
    clientSessionMaxLifespanSeconds: number | null;
    // Offline session timeouts — must match rememberMeDurationDays.
    // When a user logs in with "Stay signed in", an offline_access token is issued.
    // If these are shorter than rememberMeDurationDays, remember-me silently breaks.
    offlineSessionIdleTimeoutSeconds: number | null;
    offlineSessionMaxLifespanSeconds: number | null;
}

/**
 * Push Keycloak realm session timeout values and mirror them in MongoDB.
 */
export const syncSessionTimeouts = (
    id: string,
    timeouts: Partial<KcSessionTimeouts>
): Promise<ApiResponse<{ synced: boolean; updatedAt: string }>> =>
    post<ApiResponse<{ synced: boolean; updatedAt: string }>>(`/tenants/${id}/sync-session-timeouts`, timeouts);

// ── Admin Force Logout ───────────────────────────────────────────────────────

export interface ForceLogoutResult {
    revoked: boolean;
    userId: string;
}

/**
 * Force-logout a specific user: revoke all their active sessions in IdentityProvider
 * (which cascades to Redis). Returns immediately — the user is logged out on next request.
 */
export const forceLogoutUser = (
    tenantId: string,
    userId: string
): Promise<ApiResponse<ForceLogoutResult>> =>
    post<ApiResponse<ForceLogoutResult>>(`/tenants/${tenantId}/sessions/force-logout/${userId}`, {});

// ── Dedicated Deployment Object Types ────────────────────────────────────────

/**
 * Set the object types that route to their own dedicated DataProvider pod.
 * Pass null to revert to the platform default (Contracts and Partners).
 */
export const updateDedicatedDeploymentObjectTypes = (
    id: string,
    types: string[] | null
): Promise<ApiResponse<Tenant>> =>
    patch<ApiResponse<Tenant>>(`/tenants/${id}/dedicated-deployment-object-types`, { dedicatedDeploymentObjectTypes: types });

// ── Sub-tenant scoping ───────────────────────────────────────────────────────

/**
 * Flip the tenant-level sub-tenant scoping switch. Off by default; with it off nothing
 * sub-tenant-specific is filtered, stamped or shown for the tenant.
 */
export const updateSubtenantsEnabled = (
    id: string,
    subtenantsEnabled: boolean
): Promise<ApiResponse<Tenant>> =>
    patch<ApiResponse<Tenant>>(`/tenants/${id}/subtenants`, { subtenantsEnabled });

// ── Provisioning step ledger ─────────────────────────────────────────────────

/**
 * One entry of `tenant.provisioning.steps[]`, written by the Phase 1 provisioning
 * controller. Mirrors `ProvisioningStep` in server/controllers/tenantProvisionController.ts —
 * re-declared here because CRA's ModuleScopePlugin forbids importing outside client/src.
 */
export interface ProvisioningStep {
    id: string;
    name: string;
    status: 'done' | 'failed' | 'skipped' | 'pending';
    at: string;              // ISO-8601
    attempt: number;
    error: string | null;
    detail?: Record<string, any>;
}

// ── Tenant readiness (Phase 2 — probes only, no remediation) ────────────────
//
// Wire contract of GET /api/tenant-readiness/* (server/routes/tenantReadiness.ts +
// server/services/readiness/index.ts). Re-declared client-side for the same
// ModuleScopePlugin reason as above — never import server code.
//
// A successful aggregation is ALWAYS HTTP 200, however red the checks are. A red
// panel is data, not a request failure. 404 means the tenant does not exist.

/**
 * Five states, lowercase on the wire.
 *  ok      — probed the real world, it is correct
 *  fail    — probed the real world, it is wrong
 *  blocked — could not be probed meaningfully because an upstream check failed (see blockedBy)
 *  warn    — advisory: not right, but does not block activation
 *  unknown — could not be determined. Never green on ambiguity.
 */
export type ReadinessStatus = 'ok' | 'fail' | 'blocked' | 'warn' | 'unknown';

export interface ReadinessCheck {
    /** Stable id; also the path segment of GET /:tenantId/check/:checkId. */
    id: string;
    label: string;
    status: ReadinessStatus;
    /** One line, rendered next to the badge. */
    summary: string;
    /** Expandable detail. Always an object, never a bare string. */
    detail: Record<string, any>;
    /** Ids of the checks that blocked this one. Present only when status === 'blocked'. */
    blockedBy?: string[];
    /** Required checks gate `canActivate`. Advisory ones never do. */
    required: boolean;
}

export interface ReadinessSummary {
    ok: number;
    fail: number;
    blocked: number;
    warn: number;
    unknown: number;
}

export interface BlockingCheck {
    id: string;
    reason: string;
}

export interface TenantReadinessResult {
    tenantId: string;
    /** ISO-8601 timestamp of this aggregation. */
    checkedAt: string;
    /** True when the slow, opt-in Keycloak artifact check actually ran. */
    deep: boolean;
    /** Always 11 entries, always in READINESS_CHECK_IDS order. */
    checks: ReadinessCheck[];
    summary: ReadinessSummary;
    /** True only when EVERY required check is `ok`. */
    canActivate: boolean;
    /** Feeds the disabled-Activate tooltip. Empty when canActivate is true. */
    blockingChecks: BlockingCheck[];
}

export interface SingleCheckResult extends TenantReadinessResult {
    checkId: string;
}

/** Canonical order the server returns checks in. */
export const READINESS_CHECK_IDS = [
    'tenantRecord',
    'keycloakReachable',
    'keycloakRealm',
    'keycloakArtifacts',
    'serviceKeys',
    'encryptionKey',
    'runtimeVisibility',
    'authorizationRoles',
    'appDefinitionConfig',
    'workflowDefinitions',
    'dnsIngress',
] as const;

export type ReadinessCheckId = typeof READINESS_CHECK_IDS[number];

export interface TenantReadinessResponse {
    success: boolean;
    cached: boolean;
    persisted?: boolean;
    data: TenantReadinessResult;
    message?: string;
}

export interface CachedTenantReadinessResponse {
    success: boolean;
    cached: true;
    /** Null when this tenant has never been checked. */
    cachedAt: string | null;
    /** Null when this tenant has never been checked. */
    data: TenantReadinessResult | null;
}

export interface SingleCheckResponse {
    success: boolean;
    cached: boolean;
    checkId: string;
    /** Null when the server could not produce the row. */
    check: ReadinessCheck | null;
    persisted?: boolean;
    data: SingleCheckResult;
}

/**
 * Full readiness aggregation. `deep: true` additionally runs the Keycloak artifact
 * reconcile check, which costs 1–3 s — opt-in only, never on page load.
 */
export const getTenantReadiness = (
    tenantId: string,
    opts?: { deep?: boolean }
): Promise<TenantReadinessResponse> =>
    get<TenantReadinessResponse>(`/tenant-readiness/${tenantId}${opts?.deep ? '?deep=true' : ''}`);

/** Last persisted result with NO probing at all, so the panel paints instantly on mount. */
export const getCachedTenantReadiness = (
    tenantId: string
): Promise<CachedTenantReadinessResponse> =>
    get<CachedTenantReadinessResponse>(`/tenant-readiness/${tenantId}/cached`);

/** Re-probe a single check; the server merges it back into the cached aggregate. */
export const recheckTenantReadinessCheck = (
    tenantId: string,
    checkId: string
): Promise<SingleCheckResponse> =>
    get<SingleCheckResponse>(`/tenant-readiness/${tenantId}/check/${checkId}`);
